import React, { Component } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import AppModal from "../modal/AppModal";
import "./_alert.scss";

/**
 * Custom alert component
 * @example  <Alert show={this.state.showAlert}      | true: displays alert / false: hides alert
 *                   className="myAlert"             | custom css class (default: popupAlert)
 *                   hasHeader                       | whether modal has a header (default: false)
 *                   backdrop                        | backdrop value (same as Modal) (default: true)
 *                   title="Alert"                   | title displayed in the header (if it has one) (default: empty)
 *                   closeButton                     | close button (x) in the header (if it has one) (default: false)
 *                   acceptButtonText="Accept"       | text on accept button (default: "OK")
 *                   acceptButtonDisabled            | disable the accept button
 *                   cancelButton                    | whether there is a cancel button (default: false)
 *                   cancelButtonText="Cancel"       | text on cancel button (default: "Cancel")
 *                   secondaryButton                 | whether there is a secondary button (default: false)
 *                   secondaryButtonText             | text on secondary button
 *                   secondaryButtonDisabled         | disable the secondary button
 *                   onSecondaryButton={() => ...}   | callback for the secondary button
 *                   onClose={(bool)=>{...}}>        | callback when alert is closed. true=accept clicked, false=dismiss/cancel
 *                        {children}                 | content of the alert
 *  </Alert>
 */
export default class Alert extends Component {
  render() {
    const {
      show,
      className,
      hasHeader,
      backdrop,
      title,
      closeButton,
      acceptButtonText,
      acceptButtonDisabled,
      cancelButton,
      cancelButtonText,
      secondaryButton,
      secondaryButtonText,
      secondaryButtonDisabled,
      onSecondaryButton,
      onClose,
      maxWidth,
      children,
    } = this.props;

    const resolvedMaxWidth =
      maxWidth === undefined || maxWidth === null || maxWidth === ""
        ? undefined
        : typeof maxWidth === "number"
          ? `${maxWidth}px`
          : maxWidth;

    const handleAccept = () => {
      if (!acceptButtonDisabled) onClose(true);
    };
    const handleDismiss = () => onClose(false);
    const handleSecondary = () => {
      if (!secondaryButtonDisabled && onSecondaryButton) onSecondaryButton();
    };

    const footer = (
      <div className="app-modal__footer">
        {cancelButton ? (
          <button
            type="button"
            name="cancelButton"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={handleDismiss}
          >
            {cancelButtonText || i18n.t("messages.Cancel")}
          </button>
        ) : null}
        {secondaryButton ? (
          <button
            type="button"
            name="secondaryButton"
            className="app-modal__btn app-modal__btn--ghost"
            disabled={!!secondaryButtonDisabled}
            onClick={handleSecondary}
          >
            {secondaryButtonText || ""}
          </button>
        ) : null}
        <button
          type="button"
          name="okButton"
          className="app-modal__btn app-modal__btn--primary"
          disabled={!!acceptButtonDisabled}
          onClick={handleAccept}
        >
          {acceptButtonText || i18n.t("messages.OK")}
        </button>
      </div>
    );

    const modalStyle = resolvedMaxWidth ? { "--alert-max-width": resolvedMaxWidth } : undefined;

    return (
      <AppModal
        id="alertModal"
        className={className || "popupAlert"}
        dialogClassName="popupAlert--constrained"
        style={modalStyle}
        show={show}
        onClose={handleDismiss}
        backdrop={backdrop === undefined ? true : backdrop}
        keyboard
        // Enter key triggers accept — preserved from the legacy Alert
        // since callers (delete confirms, error dialogs) rely on it.
        onKeyUp={(e) => {
          if (e.keyCode === 13) handleAccept();
        }}
        renderHeader={hasHeader ? undefined : () => null}
        title={hasHeader ? title : undefined}
        closeButton={hasHeader && !!closeButton}
        footer={footer}
      >
        {children}
      </AppModal>
    );
  }
}

Alert.propTypes = {
  show: PropTypes.bool,
  className: PropTypes.string,
  hasHeader: PropTypes.bool,
  backdrop: PropTypes.oneOf(["static", true, false]),
  title: PropTypes.any,
  closeButton: PropTypes.bool,
  acceptButtonText: PropTypes.string,
  acceptButtonDisabled: PropTypes.bool,
  cancelButton: PropTypes.bool,
  cancelButtonText: PropTypes.string,
  secondaryButton: PropTypes.bool,
  secondaryButtonText: PropTypes.string,
  secondaryButtonDisabled: PropTypes.bool,
  onSecondaryButton: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.any,
  variant: PropTypes.oneOf([
    "success",
    "warning",
    "danger",
    "info",
    "secondary",
    "primary",
    "link",
  ]),
};
